import { AnimatedSprite, Assets, Texture } from 'pixi.js';

/**
 * One named animation: an ordered sequence of frame image URLs, played at a
 * fixed rate. This is the "flipbook" alternative to a real skeletal rig
 * (Spine) — see apps/mini-app/README.md "Pipeline Blender (flipbook)" for
 * when to use this instead of FlipbookCharacter's sibling, the Spine
 * integration described in the same README.
 */
export interface FlipbookAnimation {
  readonly name: string;
  readonly frameUrls: readonly string[];
  /** Frames per second this sequence plays at. 12 is a common "on twos" rate for 2D. */
  readonly fps: number;
  readonly loop: boolean;
}

/**
 * Builds an ordered list of frame URLs from a naming convention, so callers
 * don't have to spell out every frame by hand.
 *
 * `buildFramePaths('/characters/patch/idle', 24, 3)` produces
 * `['/characters/patch/idle_001.png', ..., '/characters/patch/idle_024.png']`
 * — matching the export contract documented in README.md.
 */
export function buildFramePaths(
  baseUrlWithoutExtension: string,
  frameCount: number,
  padding = 3,
  extension = 'png',
): string[] {
  if (frameCount <= 0) throw new Error('frameCount must be positive');
  return Array.from({ length: frameCount }, (_, index) => {
    const frameNumber = String(index + 1).padStart(padding, '0');
    return `${baseUrlWithoutExtension}_${frameNumber}.${extension}`;
  });
}

/**
 * A 2D character driven by pre-rendered frame sequences (e.g. exported from
 * Blender Grease Pencil) instead of a live skeletal rig.
 *
 * Trade-off vs. a real rig (Spine): no in-game blending between animations,
 * no dynamic posing — each animation is a fixed, pre-baked sequence. In
 * exchange, the authoring tool is free (Blender) and the player-side code is
 * simple, which is why this class exists alongside the Spine integration
 * path rather than instead of it: which one a given character uses is a
 * production decision, not a technical one this class makes.
 */
interface LoadedAnimation {
  readonly textures: readonly Texture[];
  readonly fps: number;
  readonly loop: boolean;
}

export class FlipbookCharacter {
  private readonly animations: ReadonlyMap<string, LoadedAnimation>;
  private currentName: string;
  /** The looping animation one-shot reactions fall back to when they finish. */
  private readonly restName: string;
  readonly sprite: AnimatedSprite;

  private constructor(
    animations: ReadonlyMap<string, LoadedAnimation>,
    initialName: string,
    sprite: AnimatedSprite,
  ) {
    this.animations = animations;
    this.currentName = initialName;
    this.restName = initialName;
    this.sprite = sprite;
  }

  static async load(animations: readonly FlipbookAnimation[]): Promise<FlipbookCharacter> {
    if (animations.length === 0) {
      throw new Error('FlipbookCharacter.load requires at least one animation');
    }
    const loaded = new Map<string, LoadedAnimation>();
    for (const animation of animations) {
      if (animation.frameUrls.length === 0) {
        throw new Error(`Animation "${animation.name}" has no frames`);
      }
      const textures: Texture[] = await Promise.all(
        animation.frameUrls.map((url) => Assets.load<Texture>(url)),
      );
      loaded.set(animation.name, { textures, fps: animation.fps, loop: animation.loop });
    }
    const firstName = animations[0]?.name;
    const first = firstName === undefined ? undefined : loaded.get(firstName);
    if (firstName === undefined || first === undefined) {
      throw new Error('unreachable: at least one animation was just inserted above');
    }

    const sprite = new AnimatedSprite([...first.textures]);
    sprite.anchor.set(0.5, 1);
    sprite.animationSpeed = first.fps / 60; // PixiJS ticks animationSpeed per 60fps frame
    sprite.loop = first.loop;
    sprite.play();

    return new FlipbookCharacter(loaded, firstName, sprite);
  }

  /**
   * Switches to a different named animation by swapping textures in place;
   * no-op if already playing. A non-looping animation (a one-shot reaction)
   * automatically returns to a looping animation once it completes: `returnTo`
   * if given, otherwise the resting animation. This lets a reaction settle
   * into the mood of whatever scene the player just moved into, instead of
   * always snapping back to `idle`.
   */
  play(name: string, returnTo?: string): void {
    if (name === this.currentName && returnTo === undefined) return;
    const next = this.animations.get(name);
    if (next === undefined) throw new Error(`Unknown animation: ${name}`);
    const settleTo = returnTo ?? this.restName;
    if (this.animations.get(settleTo) === undefined) {
      throw new Error(`Unknown return animation: ${settleTo}`);
    }
    this.sprite.textures = [...next.textures];
    this.sprite.animationSpeed = next.fps / 60;
    this.sprite.loop = next.loop;
    // A looping animation never completes; a one-shot settles into settleTo.
    this.sprite.onComplete = next.loop
      ? (): void => {}
      : (): void => {
          this.play(settleTo);
        };
    this.sprite.gotoAndPlay(0);
    this.currentName = name;
  }

  destroy(): void {
    this.sprite.destroy();
  }
}
